import React, { useState } from 'react';
import { Form, LocalesMenuButton, minLength, Notification, required, TextInput, useAuthState, useLogin, useNotify, useTranslate } from 'react-admin';

import { Avatar, Box, Button, CardActions, CircularProgress, Container, CssBaseline, ThemeProvider, Typography } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';
import pxLogo from '../../assets/images/chain-logo.png';
import useUpdatePassword from '../../hooks/useUpdatePassword';
import { theme } from '../../theme';
import { requiredTrim } from '../../lib/formValidators';

const containerCardSx = {
  padding: '20px',
  marginTop: '0px !important',
  backgroundColor: '#fff',
  borderRadius: '8px',
};

const containerMainSx = {
  position: 'relative' as const,
  height: '100vh',
  width: '100%',
  background: 'radial-gradient(circle,rgb(190, 241, 175) 0%,rgb(61, 188, 26) 100%)',
  padding: '20px',
  paddingTop: '40px',
};

const LoginForm = React.memo(({ handleLogin, loading }: any) => {
  const translate = useTranslate();

  return (
    <Form onSubmit={handleLogin} noValidate>
      <Container maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            ...containerCardSx,
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar style={{ width: 70, height: 70 }}>
            <img style={{ width: '100%', height: '100%' }} src={pxLogo} />
          </Avatar>
          <Typography component="h6" variant="h6">
            {translate('Área de acesso')}
          </Typography>

          <Box sx={{ mt: 1 }}>
            <TextInput
              autoFocus
              source="username"
              label={translate('ra.auth.username')}
              validate={[requiredTrim()]}
              inputProps={{ disabled: loading }}
              fullWidth
            />
            <TextInput
              source="password"
              type="password"
              label={translate('ra.auth.password')}
              validate={[requiredTrim()]}
              inputProps={{ disabled: loading }}
              fullWidth
            />
            <CardActions sx={{ padding: '0 1em 1em 1em' }}>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={loading}
                fullWidth
              >
                {loading && (
                  <CircularProgress size={25} thickness={2} />
                )}
                {translate('ra.auth.sign_in')}
              </Button>
            </CardActions>
          </Box>
        </Box>
      </Container>
    </Form>
  );
});

const ChangePasswordForm = ({ handleChangePassword, loading }: any) => {
  const translate = useTranslate();

  return (
    <Form onSubmit={handleChangePassword}>
      <Container maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            ...containerCardSx,
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar style={{ width: 70, height: 70 }}>
            <img style={{ width: '100%', height: '100%' }} src={pxLogo} />
          </Avatar>
          <Typography component="h6" variant="h6">
            {translate('Defina uma nova senha')}
          </Typography>

          <Box sx={{ mt: 1 }}>
            <TextInput
              autoFocus
              inputProps={{ disabled: loading }}
              source="password"
              type="password"
              label={translate('ra.auth.new_password')}
              validate={[minLength(6), required()]}
              fullWidth
            />
            <TextInput
              inputProps={{ disabled: loading }}
              source="passwordConfirmation"
              type="password"
              label={translate('ra.auth.confirm_password')}
              validate={[minLength(6), required()]}
              fullWidth
            />
            <Button
              color="primary"
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <></>}
            >
              {translate('Alterar senha')}
            </Button>
          </Box>
        </Box>
      </Container>
    </Form>
  );
};

const login: React.FC = () => {

  const notify = useNotify();
  const login = useLogin();
  const location = useLocation();
  const updatePassword = useUpdatePassword();
  const { isPending, authenticated } = useAuthState();

  const [loading, setLoading] = useState<boolean>(false);
  const [updatePasswordLoading, setUpdatePasswordLoading] = useState<boolean>(false);
  const [isMustChangePassword, setMustChangePassword] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<any | null>(null);

  // Chave ou texto pt-BR: o <Notification> traduz na hora de mostrar,
  // assim como o `message` que vem do backend.
  const errorMessage = {
    401: 'ra.auth.sign_in_error',
    404: 'Usuário não cadastrado',
    // Vale sobre o `message`: o limite por IP do backend responde em inglês.
    429: 'Muitas tentativas de login. Espere alguns minutos e tente de novo.',
    500: 'Erro de servidor',
  }

  const handleLogin = (auth) => {
    setLoading(true);
    login(
      auth,
      location.state ? (location.state as any).nextPathname : '/'
    ).catch(async (error: any) => {

      setLoading(false);

      if (error.mustChangePassword === true) {
        setUserInfo(error);
        setMustChangePassword(true);
        return;
      }

      notify(
        (error.status === 429 && errorMessage[429]) || error.message || (error.status && Object.keys(errorMessage).includes(error.status.toString())
          ? errorMessage[error.status]
          : 'Não foi possível realizar o login'),
        {
          type: 'error',
        }
      );
    });
  }

  const handleChangePassword = (params) => {
    const { password } = params;
    setUpdatePasswordLoading(true);
    updatePassword({
      id: userInfo.id,
      params: { password },
      user: userInfo,
      pathName: location.state ? (location.state as any).nextPathname : '/'
    }).catch((error) => {
      setUpdatePasswordLoading(false);

      notify(
        typeof error === 'string'
          ? error
          : Object.keys(errorMessage).includes(error.message)
            ? errorMessage[error.message]
            : error.message,
        {
          type: 'error',
        }
      );
    });
  }

  // Quem já tem sessão viva neste navegador não pode chegar ao formulário: logar de novo
  // gera outra chave, sobrescreve a do IndexedDB e abandona a sessão atual, que fica
  // "Ativa" na tela de acessos sem ninguém conseguir usá-la. O destino é o mesmo que o
  // useLogin usaria, para não roubar o deep link de quem caiu aqui vindo de outra tela.
  if (authenticated) {
    return <Navigate to={(location.state as any)?.nextPathname || '/'} replace />;
  }

  if (isPending) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ ...containerMainSx, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: '#fff' }} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={containerMainSx}>
        <Box sx={{ position: 'absolute', top: 16, right: 16, color: '#fff' }}>
          <LocalesMenuButton />
        </Box>
        {isMustChangePassword === false ?
          <LoginForm handleLogin={handleLogin} loading={loading} />
          :
          <ChangePasswordForm handleChangePassword={handleChangePassword} loading={updatePasswordLoading} />
        }
      </Box>
      <Notification autoHideDuration={5000} />
    </ThemeProvider>
  );
}

export default login;